import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { deltamathAPI } from "../../manager/utils";
import { timeConverter } from "../../utils";
import { EditableTextarea } from "../../shared/table/editable";
import { useDeltaToastContext } from "../../shared/contexts/ToasterContext";
import { useDocumentTitle } from "usehooks-ts";
import {
  DataTableContextProvider,
  useDataTable,
} from "./DataTable/DataTableContext";
import { DataTable } from "./DataTable/DataTable";
import { withJsonHeader } from "../../shared/axiosUtils";
import { ColumnInstance } from "react-table";
import { FilterMethods } from "./DataTable/types";
import { TableSettingsButton } from "./DataTable/TableSettingsButton";
import clsx from "clsx";
import { intersection } from "lodash";

const INVOICES_QUERY_KEY = "invoicesReport";

const OPTION_EVERYTHING_ELSE = "everythingElse";
const OPTION_PAYMENT_REQUESTED = "paymentRequested";
const TEXT_PAYMENT_REQUESTED = "CC/Check Payment Requested";

const QuoteStatusFilter: React.FC<{ column: ColumnInstance }> = ({
  column,
}) => {
  return (
    <select
      value={column.filterValue || ""}
      onChange={(e) => {
        e.preventDefault();
        if (e.target.value === OPTION_EVERYTHING_ELSE) {
          column.setFilter(OPTION_EVERYTHING_ELSE);
        } else if (e.target.value === OPTION_PAYMENT_REQUESTED) {
          column.setFilter(OPTION_PAYMENT_REQUESTED);
        } else if (e.target.value === "") {
          column.setFilter(undefined);
        }
      }}
      className="h-8 w-[230px] rounded px-2 py-0 text-sm text-dm-charcoal-600"
    >
      <option value="">All</option>
      <option value={OPTION_PAYMENT_REQUESTED}>{TEXT_PAYMENT_REQUESTED}</option>
      <option value={OPTION_EVERYTHING_ELSE}>Everything Else</option>
    </select>
  );
};

const statusFilter: FilterMethods[string] = (rows, columnId, filterValue) =>
  rows.filter((row) => {
    const value = row.values[columnId[0]] as string;
    if (filterValue === OPTION_EVERYTHING_ELSE) {
      return value !== TEXT_PAYMENT_REQUESTED;
    }
    if (filterValue === OPTION_PAYMENT_REQUESTED) {
      return value === TEXT_PAYMENT_REQUESTED;
    }
    return true;
  });

const FilterByNoCheckedButton: React.FC = () => {
  const { table } = useDataTable();
  const checkboxKeys = [
    "printed_date",
    "emailed_date",
    "mailed_date",
    "sent_ack",
  ];
  const allCheckboxFilterSet =
    intersection(
      table.state.filters.map((f) => f.id),
      checkboxKeys
    ).length === checkboxKeys.length;
  return (
    <TableSettingsButton
      className={clsx(allCheckboxFilterSet && "!border-dm-charcoal-500")}
      onClick={() => {
        if (allCheckboxFilterSet) {
          table.setAllFilters(
            table.state.filters.filter((f) => !checkboxKeys.includes(f.id))
          );
        } else {
          table.setAllFilters([
            ...table.state.filters,
            ...checkboxKeys.map((id) => ({ id, value: false })),
          ]);
        }
      }}
    >
      <i className="fal fa-square" />
      No columns checked
    </TableSettingsButton>
  );
};

const checkboxFilter: FilterMethods[string] = (rows, columnId, filterValue) =>
  rows.filter((row) => {
    const value = row.values[columnId[0]] as null | undefined | number;
    return !!value === filterValue;
  });

function InvoicesReport() {
  useDocumentTitle("Invoices Report");
  const toastContext = useDeltaToastContext();
  const queryClient = useQueryClient();

  const updateQuoteMutation = useMutation({
    mutationFn: ({
      quoteNumber,
      invoiceDocNumber,
      field,
      value,
    }: {
      quoteNumber: number;
      invoiceDocNumber: number;
      field: string;
      value: any;
    }) => {
      const payload: {
        [key: string]: number | null;
      } = {};

      if (field === "notes") {
        payload[field] = value;
      } else {
        payload[field] = value ? Math.floor(Date.now() / 1000) : null;
      }
      if (invoiceDocNumber) {
        payload["invoiceNumber"] = invoiceDocNumber;
      }

      const apiUrl = quoteNumber
        ? `${deltamathAPI()}/manager_new/invoices/${quoteNumber}`
        : `${deltamathAPI()}/manager_new/invoices/docNumber/${invoiceDocNumber}`;
      const token = localStorage.getItem("customer_service_token");
      return axios.put(
        apiUrl,
        payload,
        withJsonHeader({
          headers: { Authorization: `Bearer ${token}` },
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
    },
    onError: (error: any) => {
      if (error.response?.data?.message) {
        toastContext.addToast({
          status: "Error",
          message: error.response.data.message,
        });
      }
    },
  });

  const CheckboxCell: React.FC<{ row: any; fieldName: string }> = ({
    row,
    fieldName,
  }) => (
    <label className="inline-flex items-center gap-2 text-sm text-dm-charcoal-500">
      <input
        type="checkbox"
        defaultChecked={row.values[fieldName] ? true : false}
        onChange={(event) => {
          updateQuoteMutation.mutate({
            quoteNumber: row.original.dm_quoteNumber,
            invoiceDocNumber: row.original.DocNumber,
            field: fieldName,
            value: event.target.checked ? true : false,
          });
        }}
      />
      {!!row.original[fieldName] && timeConverter(row.original[fieldName])}
    </label>
  );

  return (
    <main className="flex flex-col gap-4 p-6">
      <h1 className="text-2xl font-bold">Invoices Report</h1>
      <DataTableContextProvider
        source="/manager_new/quickbooks/invoices"
        queryKey={INVOICES_QUERY_KEY}
        defaults={{ sortBy: [] }}
        filterable={["dm_status", "CustomerRef"]}
        filterSource="client"
        globalFilter
        downloadButton={false}
        filterOverrides={{ dm_status: QuoteStatusFilter }}
        filterMethods={{
          dm_status: statusFilter,
          printed_date: checkboxFilter,
          emailed_date: checkboxFilter,
          mailed_date: checkboxFilter,
          sent_ack: checkboxFilter,
        }}
        customButtons={[<FilterByNoCheckedButton key="no-checked" />]}
        columnOverrides={{
          printed_date: ({ row }) => (
            <CheckboxCell
              key={`printed_date_${(row as any)?.original.DocNumber}`}
              row={row}
              fieldName="printed_date"
            />
          ),
          emailed_date: ({ row }) => (
            <CheckboxCell
              key={`emailed_date_${(row as any)?.original.DocNumber}`}
              row={row}
              fieldName="emailed_date"
            />
          ),
          mailed_date: ({ row }) => (
            <CheckboxCell
              key={`mailed_date_${(row as any)?.original.DocNumber}`}
              row={row}
              fieldName="mailed_date"
            />
          ),
          sent_ack: ({ row }) => (
            <CheckboxCell
              key={`sent_ack_${(row as any)?.original.DocNumber}`}
              row={row}
              fieldName="sent_ack"
            />
          ),
          dm_notes: ({ row, cell }) => (
            <div className="w-80" key={(row as any).original.DocNumber}>
              <EditableTextarea
                value={(cell as any).value}
                row={row}
                updateMutation={updateQuoteMutation}
              />
            </div>
          ),
        }}
        fetchOnLoad
      >
        <DataTable />
      </DataTableContextProvider>
    </main>
  );
}

export default InvoicesReport;
